.home {
  min-height: 100vh;
}

.hero {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

.heroContent {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: var(--qs-space-32);
  max-width: var(--qs-layout-column-reading);
  margin: 0 auto;
  gap: var(--qs-space-48);
}

.title {
  font-family: var(--qs-font-display);
  font-style: italic;
  font-weight: 300;
  font-size: var(--qs-type-display-xl-size);
  line-height: var(--qs-type-display-xl-lh);
  margin: 0;
  color: var(--qs-color-text);

  // The hero `em` stays italic inside the italic title, at the same weight (spec § Notes).
  em {
    font-style: italic;
    font-weight: inherit;
  }
}

.subtitle {
  font-family: var(--qs-font-text);
  font-weight: 300;
  font-size: var(--qs-type-reading-size);
  line-height: var(--qs-type-reading-lh);
  color: var(--qs-color-text-soft);
  margin: 0;
  max-width: 44ch;
}

.heroActions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--qs-space-16);
}

.heroHint {
  font-family: var(--qs-font-text);
  font-size: var(--qs-type-small-size);
  line-height: var(--qs-type-small-lh);
  color: var(--qs-color-text-muted);
  margin: 0;
}

.explore {
  padding: var(--qs-space-96) var(--qs-space-32);
  max-width: var(--qs-layout-column-wide);
  margin: 0 auto;
}

.exploreHeading {
  font-family: var(--qs-font-display);
  font-style: italic;
  font-weight: 300;
  font-size: var(--qs-type-display-m-size);
  line-height: var(--qs-type-display-m-lh);
  margin: 0 0 var(--qs-space-40);
  text-align: center;
  color: var(--qs-color-text);
}

.exploreGrid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--qs-space-16);

  @media (max-width: 719px) {
    grid-template-columns: 1fr;
  }
}

.exploreCard {
  display: flex;
  flex-direction: column;
  gap: var(--qs-space-8);
  padding: var(--qs-space-20) var(--qs-space-16);
  background: var(--qs-color-surface);
  border: 1px solid var(--qs-color-line);
  border-radius: var(--qs-radius-md);
  text-decoration: none;
  transition: border-color var(--qs-motion-dur-fade) var(--qs-motion-ease-settle),
    background var(--qs-motion-dur-fade) var(--qs-motion-ease-settle);

  &:hover {
    border-color: var(--qs-color-accent);
    background: var(--qs-color-surface-raised);
  }
}

.exploreCardLabel {
  font-family: var(--qs-font-display);
  font-style: italic;
  font-size: var(--qs-type-display-xs-size);
  line-height: var(--qs-type-display-xs-lh);
  color: var(--qs-color-text);
}

.exploreCardDesc {
  font-family: var(--qs-font-text);
  font-weight: 300;
  font-size: var(--qs-type-body-size);
  line-height: var(--qs-type-body-lh);
  color: var(--qs-color-text-muted);
}

.prose {
  max-width: var(--qs-layout-column-reading);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--qs-space-24);

  p {
    font-family: var(--qs-font-text);
    font-weight: 300;
    font-size: var(--qs-type-reading-size);
    line-height: var(--qs-type-reading-lh);
    color: var(--qs-color-text-soft);
    margin: 0;
  }
}

.ctaSection {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--qs-space-24);
  max-width: var(--qs-layout-column-reading);
  margin: 0 auto;
  padding: var(--qs-space-64) var(--qs-space-32);
  text-align: center;
}

.pricing {
  padding: var(--qs-space-96) var(--qs-space-32);
  max-width: var(--qs-layout-page-max);
  margin: 0 auto;
}

.pricingHeader {
  text-align: center;
  margin-bottom: var(--qs-space-48);

  h2 {
    font-family: var(--qs-font-display);
    font-style: italic;
    font-weight: 300;
    font-size: var(--qs-type-display-l-size);
    line-height: var(--qs-type-display-l-lh);
    margin: 0 0 var(--qs-space-16);
    color: var(--qs-color-text);
  }

  p {
    font-family: var(--qs-font-text);
    font-weight: 300;
    font-size: var(--qs-type-reading-size);
    line-height: var(--qs-type-reading-lh);
    color: var(--qs-color-text-soft);
    margin: 0;
  }
}

.pricingList {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--qs-space-32);
}

.pricingNote {
  margin: var(--qs-space-32) 0 0;
  text-align: center;
  font-size: var(--qs-type-small-size);
  color: var(--qs-color-text-soft);
}

// Phone: stacks with the emphasised item first (decisions.md §34) — the emphasised
// `PricingItem` sets `order: -1` on itself; nothing to do here but let it stack.
@media (max-width: 719px) {
  .pricingList {
    flex-direction: column;
    align-items: stretch;
  }
}

.hashTarget {
  scroll-margin-top: var(--qs-layout-navbar-height);
}

.faq {
  padding: var(--qs-space-96) var(--qs-space-32);
  max-width: var(--qs-layout-column-reading);
  margin: 0 auto;

  h2 {
    font-family: var(--qs-font-display);
    font-style: italic;
    font-weight: 300;
    font-size: var(--qs-type-display-l-size);
    line-height: var(--qs-type-display-l-lh);
    margin: 0 0 var(--qs-space-48);
    text-align: center;
    color: var(--qs-color-text);
  }
}

.faqList {
  display: flex;
  flex-direction: column;
  gap: var(--qs-space-16);
}

.faqItem {
  border: 1px solid var(--qs-color-line);
  border-radius: var(--qs-radius-md);
  background: var(--qs-color-surface);
  overflow: hidden;
}

.faqQuestionHeading {
  margin: 0;
}

.faqQuestion {
  width: 100%;
  min-height: var(--qs-target-min);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--qs-space-16);
  padding: var(--qs-space-20) var(--qs-space-24);
  background: transparent;
  border: none;
  text-align: start;
  font-family: var(--qs-font-text);
  font-size: var(--qs-type-display-xs-size);
  line-height: var(--qs-type-display-xs-lh);
  color: var(--qs-color-text);
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--qs-color-focus);
    outline-offset: -2px;
  }
}

.faqChevron {
  flex: none;
  color: var(--qs-color-text-muted);
  transition: transform var(--qs-motion-dur-fade) var(--qs-motion-ease-settle);
}

.faqChevronOpen {
  transform: rotate(180deg);
}

// Closed answers stay in the DOM (spec § 7): the row collapses to 0fr, which needs no height cap
// for a long answer, and `visibility` takes them out of the reading order as height alone does not.
.faqAnswer {
  display: grid;
  grid-template-rows: 0fr;
  visibility: hidden;
  transition: grid-template-rows var(--qs-motion-dur-settle) var(--qs-motion-ease-settle),
    visibility var(--qs-motion-dur-settle);

  > div {
    min-height: 0;
    overflow: hidden;
  }

  p {
    font-family: var(--qs-font-text);
    font-weight: 300;
    font-size: var(--qs-type-reading-size);
    line-height: var(--qs-type-reading-lh);
    color: var(--qs-color-text-soft);
    margin: 0;
    padding: 0 var(--qs-space-24) var(--qs-space-20);
  }
}

.faqAnswerOpen {
  grid-template-rows: 1fr;
  visibility: visible;
}

@media (prefers-reduced-motion: reduce) {
  .faqAnswer {
    transition: none;
  }
}

@media (max-width: 719px) {
  .explore,
  .pricing,
  .faq,
  .ctaSection {
    padding-top: var(--qs-space-64);
    padding-bottom: var(--qs-space-64);
  }
}
